import { ComponentChildren, ComponentProps, Ref } from 'preact';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'preact/compat';

import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { CheckboxControl } from '#PRIVATE_CheckboxControl';
import { useId } from '#hooks/UNSAFE_useId';
import { CheckboxRadioContext } from '#PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { AriaAttributesSignalExcluded } from '#utils/UNSAFE_attributeUtils';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: AriaAttributesSignalExcluded['aria-describedby'];
  /**
   * Text to provide the user with additional information.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Content associated with checkbox. This can be a label or a block of text.
   */
  children: ComponentChildren;
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the checkbox requires a selection to be made.
   */
  isRequired?: boolean;
  /**
   * Specifies if the checkbox is read-only.
   */
  isReadonly?: boolean;
  /**
   * Specifies if the checkbox is disabled.
   */
  isDisabled?: boolean;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * Callback invoked each time the user changes checkbox selection.
   */
  onCommit: (detail: ValueUpdateDetail<boolean>) => void;
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Value of the checkbox.
   */
  value?: boolean;
};

/**
 * Checkbox is a single standalone checkbox control. It represents a boolean value.
 */
export const Checkbox = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      messages,
      onCommit,
      userAssistanceDensity: propUserAssistanceDensity,
      value: propValue,
      children
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();

    const checkboxRef = useRef<HTMLInputElement>(null);
    const id = useId();

    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;
    const isEditable = !isDisabled && !isReadonly;
    const bottomUaId = isEditable ? `${id}-bottom-ua` : undefined;

    const onChangeHandler = useCallback(
      (e: Event) => {
        // return early on keyup if key isn't spacebar
        const { key } = e as KeyboardEvent;
        const hasKey = key !== undefined;

        if (hasKey && key !== ' ') {
          return;
        }

        const input = e.target as HTMLInputElement;
        // While mouse clicking StyledCheckbox results in toggling the checked state on an input element, spacebar key events do not.
        // If it is a spacebar key event, we will return the toggled value of its current state.
        const value = hasKey ? !input.checked : input.checked;
        const previousValue = !!propValue;

        if (previousValue !== value) {
          onCommit({
            value,
            previousValue
          });
        }
      },
      [onCommit, propValue]
    );

    useImperativeHandle(ref, () => ({
      focus: () => {
        !isDisabled && checkboxRef.current?.focus();
      },
      blur: () => {
        const activeElement = document.activeElement as HTMLElement;
        checkboxRef.current === activeElement && activeElement.blur();
      }
    }));

    // TODO fix issue with tooltip and link in label
    // 

    return (
      <CheckboxRadioContext.Provider value={{ isFocusRingShown: !isDisabled }}>
        <CheckboxControl
          {...(isEditable
            ? {
                onChange: onChangeHandler,
                onKeyUp: onChangeHandler
              }
            : {})}
          aria-describedby={ariaDescribedBy}
          assistiveText={assistiveText}
          columnSpan={columnSpan}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          isChecked={propValue}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          isRequired={isRequired}
          ref={checkboxRef}>
          {children}
        </CheckboxControl>
        {isEditable && (
          <InlineUserAssistance
            id={bottomUaId}
            isRequiredShown={!!isRequired && !propValue}
            requiredAlignment="start"
            messages={messages}
            userAssistanceDensity={
              userAssistanceDensity === 'reflow' ? userAssistanceDensity : 'efficient'
            }
          />
        )}
      </CheckboxRadioContext.Provider>
    );
  }
);
